
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cancelSubscription } from '@/services/stripe';
import { 
  Dialog, 
  DialogTrigger, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface CancelSubscriptionDialogProps {
  subscriptionId: string | undefined;
  onCancellationSuccess?: () => void;
}

const CancelSubscriptionDialog: React.FC<CancelSubscriptionDialogProps> = ({
  subscriptionId,
  onCancellationSuccess
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  const handleCancelSubscription = async () => {
    if (!subscriptionId) {
      toast("Erreur", {
        description: "Impossible d'annuler cet abonnement"
      });
      return;
    }
    
    setIsCancelling(true);
    
    try {
      const result = await cancelSubscription(subscriptionId);
      
      if (!result.success) {
        toast("Erreur", {
          description: result.error || "Impossible d'annuler l'abonnement"
        });
        return;
      }
      
      toast("Abonnement annulé", {
        description: "Votre abonnement a été annulé et expirera à la fin de la période en cours"
      });
      
      setShowCancelDialog(false);
      
      if (onCancellationSuccess) {
        onCancellationSuccess();
      } else {
        // Force reload to update subscription status
        window.location.reload();
      }
    } catch (error) {
      toast("Erreur", {
        description: "Une erreur est survenue lors de l'annulation de l'abonnement"
      });
      console.error('Error cancelling subscription:', error);
    } finally {
      setIsCancelling(false);
    }
  };
  
  return (
    <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-destructive border-destructive hover:bg-destructive/10">
          Annuler l'abonnement
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Annuler votre abonnement ?</DialogTitle>
          <DialogDescription>
            Votre abonnement restera actif jusqu'à la fin de la période en cours, puis sera automatiquement annulé.
            Vous perdrez l'accès à toutes les fonctionnalités premium.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
            Retour
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleCancelSubscription}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Annulation...
              </>
            ) : (
              'Confirmer l\'annulation'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelSubscriptionDialog;
